import { Fragment, useState } from "react"
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import Loader from "../../../components/Loader"
import ApiError from "../../../components/ApiError"
import Pagination from "../../../components/Pagination"
import { formatISODate } from "../../../utils/datetime"
import {
  STAGE_BUCKETS,
  centerStatusOptions,
  unifiedStageOptions,
  statusMeta,
  toneStyles,
  toneDot,
  sourceTagLabel,
  getUnifiedStage,
} from "../statusConfig"

const centerStatusMeta = Object.fromEntries(
  centerStatusOptions.map((s) => [s.value, s])
)

const MiniStageBar = ({ cases }) => {
  const total = cases.length
  if (!total) return <div className="h-3 w-full rounded-full bg-gray-100" />

  const counts = STAGE_BUCKETS.map((bucket) => ({
    ...bucket,
    count: cases.filter((c) => bucket.test(getUnifiedStage(c))).length,
  })).filter((b) => b.count > 0)

  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100">
      {counts.map((b) => (
        <span
          key={b.key}
          className={toneDot[b.tone]}
          style={{ width: `${(b.count / total) * 100}%` }}
          title={`${b.label}: ${b.count}`}
        />
      ))}
    </div>
  )
}

const StageBucketLegend = () => (
  <div className="flex flex-wrap gap-4 border-t border-gray-100 px-4 py-3">
    {STAGE_BUCKETS.map((b) => (
      <div
        key={b.key}
        className="flex items-center gap-1.5 text-xs text-gray-500"
      >
        <span className={`h-2.5 w-2.5 rounded-sm ${toneDot[b.tone]}`} />
        {b.label}
      </div>
    ))}
  </div>
)

const CenterStatusPill = ({ status }) => {
  const meta = centerStatusMeta[status]
  if (!meta) return null
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${
        meta.tone === "amber"
          ? "border-amber-300 bg-amber-50 text-amber-800"
          : meta.tone === "emerald"
            ? "border-emerald-300 bg-emerald-100 text-emerald-800"
            : "border-sky-200 bg-sky-50 text-secondary_dark"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${toneDot[meta.tone]}`} />
      {meta.label}
    </span>
  )
}

// Current stage of a case: the satisfaction ticket's stage takes priority
// (it's always further along once it exists), else the case ticket's
// stage, else "no ticket" — same priority getUnifiedStage already encodes.
const StageChip = ({ study }) => {
  const n = getUnifiedStage(study)
  if (n === null || n === undefined) {
    return <span className="text-sm text-gray-400">No ticket</span>
  }

  const stage = unifiedStageOptions.find((s) => s.n === n)
  const tone = statusMeta[stage.value].tone
  const source = study.CustomerSatisfactionStatus
    ? study.CustomerSatisfactionStatusSource
    : study.TrackingTicketStatusSource

  return (
    <div className="flex flex-col gap-1">
      <span
        className={`inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${toneStyles[tone]}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${toneDot[tone]}`} />
        {stage.label}
      </span>
      {source && (
        <span className="text-[11px] text-gray-400">
          {sourceTagLabel[source]}
        </span>
      )}
    </div>
  )
}

const CaseTable = ({ cases }) => {
  const sorted = [...cases].sort(
    (a, b) => (getUnifiedStage(b) ?? -1) - (getUnifiedStage(a) ?? -1)
  )

  return (
    <table className="w-full border-separate border-spacing-0">
      <thead>
        <tr>
          {[
            "Accession",
            "Modality",
            "Referring Doctor",
            "Stage",
            "Tracked Since",
          ].map((h) => (
            <th
              key={h}
              className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-400"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((c) => (
          <tr key={c.OnboardingStudyTrackingId}>
            <td className="border-t border-gray-100 px-3 py-2.5 font-mono text-sm text-gray-700">
              {c.AccessionNo || "—"}
            </td>
            <td className="border-t border-gray-100 px-3 py-2.5">
              {c.Modality ? (
                <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[11.5px] font-semibold text-gray-700">
                  {c.Modality}
                </span>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </td>
            <td className="border-t border-gray-100 px-3 py-2.5 text-sm text-gray-700">
              {c.ReferringPhysician || "—"}
            </td>
            <td className="border-t border-gray-100 px-3 py-2.5 align-top">
              <StageChip study={c} />
            </td>
            <td className="border-t border-gray-100 px-3 py-2.5 text-sm text-gray-500">
              {formatISODate(c.CreatedAt, "MMM d, yyyy") || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const CentersList = ({
  data,
  total,
  isLoading,
  isError,
  error,
  pagination,
  pageCount,
  onPaginationChange,
  studyLimit,
}) => {
  const [expandedCenterId, setExpandedCenterId] = useState(null)

  const table = useReactTable({
    columns: [],
    data,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange,
    pageCount,
    state: { pagination },
  })

  const toggleCenter = (id) =>
    setExpandedCenterId((prev) => (prev === id ? null : id))

  if (isError) {
    return (
      <div className="rounded-xl border border-gray-200 p-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center rounded-xl border border-gray-200 py-16">
        <Loader />
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 py-16 text-center text-sm text-gray-500">
        No centers match the current filters.
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-gray-200">
      <div className="divide-y divide-gray-100">
        {data.map((center) => {
          const isExpanded = expandedCenterId === center.CustomerOnboardingId
          const cases = center.Cases || []
          const caseCount = center.CaseCount ?? cases.length
          const atCap = Number.isFinite(studyLimit) && caseCount >= studyLimit

          return (
            <Fragment key={center.CustomerOnboardingId}>
              <button
                aria-expanded={isExpanded}
                className="grid w-full grid-cols-[1.7fr_.9fr_1.5fr_140px_20px] items-center gap-4 px-4 py-3.5 text-left hover:bg-gray-50"
                type="button"
                onClick={() => toggleCenter(center.CustomerOnboardingId)}
              >
                <div className="min-w-0">
                  <div className="truncate text-[14.5px] font-semibold text-gray-900">
                    {center.PatientIssuerName || "—"}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-400">
                    Tracking since{" "}
                    {formatISODate(center.OnboardedAt, "MMM d, yyyy")}
                  </div>
                </div>
                <div className="text-sm text-gray-700">
                  <b className="text-gray-900">{caseCount}</b> case
                  {caseCount === 1 ? "" : "s"}
                  {Number.isFinite(studyLimit) && (
                    <div className="text-[11px] text-gray-400">
                      {atCap ? "cap reached" : `of ${studyLimit}`}
                    </div>
                  )}
                </div>
                <MiniStageBar cases={cases} />
                <div className="justify-self-start">
                  <CenterStatusPill status={center.Status} />
                </div>
                <FontAwesomeIcon
                  className={`h-3.5 w-3.5 justify-self-end text-gray-400 transition-transform ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                  icon={faChevronDown}
                />
              </button>

              {isExpanded && (
                <div className="overflow-x-auto bg-gray-50 px-4 pb-4 pt-1">
                  <CaseTable cases={cases} />
                </div>
              )}
            </Fragment>
          )
        })}
      </div>

      <StageBucketLegend />

      <div className="flex items-center justify-between border-t border-gray-100 px-4">
        <span className="py-3 text-xs text-gray-500">
          Showing <span className="font-semibold">{data.length}</span> of{" "}
          <span className="font-semibold">{total}</span> centers
        </span>
        <Pagination tableLib={table} />
      </div>
    </div>
  )
}

export default CentersList
